import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import type { PlaylistItem, Playlist } from '@jwp/ott-common/types/playlist';
import { useUIStore } from '@jwp/ott-common/src/stores/UIStore';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import useFirstRender from '@jwp/ott-hooks-react/src/useFirstRender';
import useSearchQueryUpdater from '@jwp/ott-ui-react/src/hooks/useSearchQueryUpdater';
import { useSearch } from '@jwp/ott-hooks-react/src/useSearch';
import useOpaqueId from '@jwp/ott-hooks-react/src/useOpaqueId';
import { debounce } from 'lodash';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useUIControls } from '@jwp/ott-hooks-react/src/useUIControls';
import ReactGA from 'react-ga4';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import _ from 'lodash';

import { PLAYLIST_LIMIT } from '../../../../../packages/common/src/constants';
import CardGrid from '../../components/CardGrid/CardGrid';
import ErrorPage from '../../components/ErrorPage/ErrorPage';
import RecentSearches from '../../components/RecentSearches/RecentSearches';
import Button from '../../components/Button/Button';
import NoResultsIcon from '@jwp/ott-theme/assets/icons/no_result.svg?react';

import styles from './Search.module.scss';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

const Search = () => {
  const { t } = useTranslation('search');
  const breakpoint = useBreakpoint();

  const { config, accessModel } = useConfigStore(({ config, accessModel }) => ({ config, accessModel }), shallow);
  const { siteName, features } = config;
  const { isGoogleAnalytics, isAnalyticsEnable } = useAppConfig();
  const headingId = useOpaqueId('search_heading');

  const firstRender = useFirstRender();
  const { singleClickPlayEnabled } = useUIControls();
  const searchQuery = useUIStore((state) => state.searchQuery);
  const { updateSearchQuery } = useSearchQueryUpdater();
  const params = useParams();
  const query = params['*'];
  const location = useLocation();

  const INITIAL_PAGE_OFFSET = 1;
  const globalSearchId = config?.features?.searchPlaylist;

  const [page_offset, setPage_offset] = useState(INITIAL_PAGE_OFFSET);
  const { isFetching, error, data: playlist } = useSearch(query || '', String(PLAYLIST_LIMIT), String(page_offset));
  const [allSearchedPlaylists, setAllSearchedPlaylists] = useState<Playlist>();
  // User
  const { user, subscription } = useAccountStore(({ user, subscription }) => ({ user, subscription }), shallow);
  const isLoggedIn = !!user;
  const userId = isLoggedIn ? user.id : 'Guest user';
  const deviceType = useGADeviceType();
  const hasScrolled = useRef(false);

  const getURL = (playlistItem: PlaylistItem) =>
    mediaURL({
      id: playlistItem.mediaid,
      title: playlistItem.title,
      playlistId: features?.searchPlaylist,
      play: singleClickPlayEnabled,
      contentType: playlistItem.contentType,
      returnPath: location.pathname + location.search,
    });
  // Update the search bar query to match the route param on mount
  useEffect(() => {
    setPage_offset(INITIAL_PAGE_OFFSET);
    setAllSearchedPlaylists(undefined);
    if (!firstRender) {
      return;
    }

    if (query && query !== searchQuery) {
      updateSearchQuery(query);
    }
  }, [firstRender, query, searchQuery, updateSearchQuery]);

  useEffect(() => {
    sendGAEvent('screen_view', {
      firebase_screen_class: 'Search',
      firebase_screen: 'Search',
      device_type: deviceType,
      userid_no: userId,
    });

    return () => {
      useUIStore.setState({
        searchQuery: '',
        searchActive: false,
      });
    };
  }, []);

  useEffect(() => {
    if (query && playlist?.playlist.length) {
      sendGAEvent('search_performed', {
        query,
        result_count: playlist.playlist.length,
        userid_no: userId,
        device_type: deviceType,
      });
    } else if (query && !playlist?.playlist.length) {
      sendGAEvent('search_performed', {
        query,
        result_count: 0,
        userid_no: userId,
        device_type: deviceType,
      });
    }
  }, [isGoogleAnalytics, query, playlist, userId, deviceType]);

  useEffect(() => {
    if (!isAnalyticsEnable) {
      return;
    }

    let lastScrollTop = 0;

    const handleScroll = _.throttle(() => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const direction = scrollTop > lastScrollTop ? 'down' : 'up';
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

      if (scrollTop > 0) {
        sendGAEvent('scroll', {
          scroll_direction: direction,
          userid_no: userId,
          device_type: deviceType,
        });
      }
    }, 1000);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Define the debounced function inside the effect
    const debouncedUpdateHistory = debounce((query) => {
      const historyKey = 'searchHistory';
      const maxHistory = 5;

      const currentHistory = JSON.parse(localStorage.getItem(historyKey) || '[]');
      if (!currentHistory.includes(query)) {
        const updatedHistory = [query, ...currentHistory].slice(0, maxHistory);
        localStorage.setItem(historyKey, JSON.stringify(updatedHistory));
      } else {
        const filteredHistory = currentHistory.filter((item: string) => item !== query);
        const updatedHistory = [query, ...filteredHistory].slice(0, maxHistory);
        localStorage.setItem(historyKey, JSON.stringify(updatedHistory));
      }
    }, 2000); // Delay of 500ms

    // Only call debouncedUpdateHistory when the query changes and playlist is valid
    if (query && playlist?.playlist.length) {
      debouncedUpdateHistory(query);
    }

    // Cleanup function to cancel debounce on unmount
    return () => {
      debouncedUpdateHistory.cancel();
    };
  }, [query, playlist]); // Dependencies are query and playlist

  useEffect(() => {
    if (playlist?.playlist && query === searchQuery) {
      const filteredItems = playlist.playlist.filter((item) => !item.mediaid?.startsWith('placeholder_'));
      setAllSearchedPlaylists((prev) => {
        if (!prev) {
          return { ...playlist, playlist: filteredItems };
        }
        const combinedItems = [...prev.playlist, ...filteredItems];
        return {
          ...prev,
          ...playlist,
          playlist: combinedItems,
        };
      });
    }
  }, [playlist, query, searchQuery]);

  const handleLoadMoreClick = async () => {
    setPage_offset((prev) => prev + PLAYLIST_LIMIT);
  };

  if ((error || !playlist) && !isFetching) {
    return (
      <ErrorPage title={t('error_heading')}>
        <h2 className={styles.subHeading}>{t('error_subheading')}</h2>
        <p>{t('error_description')}</p>
      </ErrorPage>
    );
  }

  if (!query) {
    return (
      <>
        <RecentSearches onQueryChange={(searchTerm: string) => updateSearchQuery(searchTerm)} />
      </>
    );
  }

  if (!isFetching && playlist && !playlist.playlist.length) {
    return (
      <div className={styles.emptyStatePage}>
        <div className={styles.emptyState}>
          <NoResultsIcon className={styles.emptyStateIcon} aria-hidden="true" />
          <p className={styles.emptyStateText}>
            {t('no_results_heading', { query, defaultValue: `No results found for "${query}"` })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.search}>
      <Helmet>
        <title>
          {t('title', { count: playlist.playlist.length, query })} - {siteName}
        </title>
      </Helmet>
      <header className={styles.header}>
        <h2 id={headingId}>
          {t('heading')} {`"${query}"`}
        </h2>
      </header>
      {allSearchedPlaylists && (
        <CardGrid
          aria-labelledby={headingId}
          getUrl={getURL}
          playlist={allSearchedPlaylists}
          isLoading={firstRender}
          accessModel={accessModel}
          isLoggedIn={!!user}
          hasSubscription={!!subscription}
        />
      )}
      {allSearchedPlaylists?.links?.next && (
        <Button
          label={t('common:load_more')}
          aria-label={t('loadMore')}
          onClick={handleLoadMoreClick}
          color={'default'}
          fullWidth={breakpoint < Breakpoint.md}
          size="large"
          type="submit"
        />
      )}
    </div>
  );
};

export default Search;
